<template>
  <div class="flex items-center gap-2 px-3 min-w-0">
    <span class="flex items-center justify-center shrink-0 size-8 rounded-full bg-apodisPink text-darkPurple text-xs font-semibold">
      {{ initials }}
    </span>
    <div class="flex flex-col min-w-0 flex-1">
      <button
        type="button"
        class="text-left text-sm font-semibold text-gray-dark truncate hover:text-primary hover:underline"
        @click="emit('open')"
      >
        {{ consumer.name || 'Patient' }}
      </button>
      <span v-if="consumerGroups.length" class="text-xs text-gray-dark truncate">
        {{ consumerGroups[0] }}
        <span v-if="consumerGroups.length > 1" class="font-semibold text-primary">+{{ consumerGroups.length - 1 }}</span>
      </span>
    </div>
    <div class="flex items-center gap-1 shrink-0">
      <Tooltip v-if="hasValidation">
        <TooltipTrigger as-child>
          <span class="flex">
            <CircleCheck v-if="consumer.groups.excluded.length" class="size-4 text-secondary-darkest" />
            <CircleX v-else class="size-4 text-red-light" />
          </span>
        </TooltipTrigger>
        <TooltipContent class="bg-darkPurple text-white">
          {{ validationLabel }}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger as-child>
          <span class="flex">
            <BellOff v-if="notificationDisabled" class="size-4 text-gray" />
            <Bell v-else class="size-4 text-primary" />
          </span>
        </TooltipTrigger>
        <TooltipContent class="bg-darkPurple text-white max-w-xs">
          Notification comptoir {{ notificationDisabled ? 'désactivée' : 'activée' }}.
          {{ notificationDisabled ? '' : 'Alerte le pharmacien au prochain passage du patient.' }}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger as-child>
          <button
            type="button"
            class="flex rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Ouvrir la messagerie du patient"
            @click="emit('conversation')"
          >
            <MessageCircle class="size-4 text-primary hover:opacity-75" />
          </button>
        </TooltipTrigger>
        <TooltipContent class="bg-darkPurple text-white">
          Ouvrir la messagerie du patient
        </TooltipContent>
      </Tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from "vue";
import dayjs from "dayjs";
import {Bell, BellOff, CircleCheck, CircleX, MessageCircle} from "@lucide/vue";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/core/components/ui/tooltip";
import {SupportProgramMeta, SupportProgramPatient} from "@/program/interfaces/Program";

const props = defineProps<{
    consumer: SupportProgramPatient;
    /** The followed targeting groups labels, by slug. */
    groups: Record<string, string>;
    meta: SupportProgramMeta;
    /** Whether the program has validating groups, to show the validation status. */
    hasValidation: boolean;
}>();

const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'conversation'): void;
}>();

const initials = computed(() => (props.consumer.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join(''));

const consumerGroups = computed(() => props.consumer.groups.included
    .filter(group => group.slug in props.groups)
    .map(group => props.groups[group.slug]));

const validationLabel = computed(() => {
    const groups = props.consumer.groups.excluded;

    if (!groups.length) {
        return 'Aucune vente validante';
    }

    const labels = groups.map(group => props.meta.excluded[group.slug] ?? group.slug).join(', ');
    const last = props.consumer.last_visits?.excluded;

    return last ? `${labels}, dernière le ${dayjs(last).format('DD/MM/YYYY')}` : labels;
});

const notificationDisabled = computed(() => props.consumer.settings.some(setting => setting.disabled)
    || props.consumer.feedbacks.some(feedback => feedback.hide_notification));
</script>
